import React from 'react';
import { Link } from 'react-router-dom';

const RefundPolicy: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero section */}
      <div className="relative bg-gradient-to-r from-blue-50 to-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
              Refund Policy
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-500">
              Last Updated: April 1, 2025
            </p>
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-blue mx-auto">
            <h2>1. Introduction</h2>
            <p>
              At Explanatum Limited, we strive to ensure your satisfaction with our services. This Refund Policy outlines our procedures and guidelines regarding refunds for our subscription services. By using our services, you agree to the terms of this Refund Policy.
            </p>

            <h2>2. Payment Processing</h2>
            <p>
              We use Paddle.com as our payment processor and merchant of record. All payments are processed securely through Paddle.com, and your purchase transaction is between you and Paddle.com. As such, refund requests are processed in accordance with both our policy and Paddle's terms of service.
            </p>

            <h2>3. Subscription Refunds</h2>
            <p>
              3.1 <strong>7-Day Free Trial</strong>: We offer a 7-day free trial for new subscriptions. During this trial period, you can explore all features of our platform at no cost. Your payment method will be charged automatically when the trial period ends unless you cancel before the trial expiration date.
            </p>
            <p>
              3.2 <strong>Renewal Payments</strong>: Subscription renewals are generally not eligible for refunds. We recommend canceling your subscription before the renewal date if you no longer wish to use our services.
            </p>
            <p>
              3.3 <strong>Pro-rated Refunds</strong>: For annual subscriptions, we may, at our discretion, offer a pro-rated refund for the unused portion of your subscription. This is assessed on a case-by-case basis.
            </p>

            <h2>4. Exceptions to the Refund Policy</h2>
            <p>
              4.1 <strong>Service Unavailability</strong>: If our services are unavailable for a significant period (more than 24 consecutive hours) due to technical issues on our end, you may be eligible for a partial refund proportional to the duration of the service outage.
            </p>
            <p>
              4.2 <strong>Account Termination</strong>: If we terminate your account for violations of our Terms of Service, you will not be eligible for a refund.
            </p>
            <p>
              4.3 <strong>Fraudulent Activity</strong>: Refunds will not be issued in cases of suspected fraudulent activity.
            </p>

            <h2>5. How to Request a Refund</h2>
            <p>
              5.1 <strong>Contact Customer Support</strong>: To request a refund, please contact our customer support team at support@explanatum.co.uk with the following information:
            </p>
            <ul>
              <li>Your full name</li>
              <li>Email address associated with your account</li>
              <li>Date of purchase</li>
              <li>Reason for requesting a refund</li>
              <li>Order ID or receipt number (if available)</li>
            </ul>
            <p>
              5.2 <strong>Processing Time</strong>: Refund requests are typically processed within 5-7 business days. Once approved, the refund will be issued to the original payment method used for the purchase. Depending on your payment provider, it may take an additional 5-10 business days for the refund to appear in your account.
            </p>

            <h2>6. Paddle.com Refund Process</h2>
            <p>
              As Paddle.com is our merchant of record, all refunds are processed through their system. In some cases, you may need to contact Paddle.com directly to complete the refund process. Their support team can be reached through their website at <a href="https://www.paddle.com/support" target="_blank" rel="noopener noreferrer">https://www.paddle.com/support</a>.
            </p>

            <h2>7. Cancellation Policy</h2>
            <p>
              7.1 <strong>How to Cancel</strong>: You can cancel your subscription at any time by:
            </p>
            <ul>
              <li>Logging into your account and navigating to the subscription settings</li>
              <li>Contacting our customer support team at support@explanatum.co.uk</li>
              <li>Using the cancellation link provided in your subscription confirmation email</li>
            </ul>
            <p>
              7.2 <strong>Effect of Cancellation</strong>: When you cancel your subscription, you will continue to have access to the service until the end of your current billing period. After that, your subscription will not renew, and your access to premium features will end.
            </p>

            <h2>8. Changes to This Policy</h2>
            <p>
              We reserve the right to modify this Refund Policy at any time. Any changes will be effective immediately upon posting the updated policy on our website. We encourage you to review this policy periodically for any changes.
            </p>

            <h2>9. Contact Us</h2>
            <p>
              If you have any questions about this Refund Policy, please contact us at:
            </p>
            <p>
              Explanatum Limited<br />
              Email: support@explanatum.co.uk<br />
              Address: 3 Heathmoor Road, Wirral, CH46 7UN<br />
            </p>
          </div>
        </div>
      </div>

      {/* CTA section */}
      <div className="bg-blue-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Have questions about our refund policy?</span>
            <span className="block text-blue-200">Our support team is here to help.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 transition-all duration-200 ease-in-out"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
